import { createTheme, Theme as MuiTheme } from '@mui/material/styles';

import '@mui/x-date-pickers/themeAugmentation';

import { PickerTextField } from 'components/Input/PickerTextField';

import { colors } from './colors';

// Applied to every picker so clicking the field opens it, not just the calendar icon.
const openOnFieldClick = { defaultProps: { slots: { textField: PickerTextField } } };

export const Theme: MuiTheme = createTheme({
  palette: {
    primary: colors.primary,
    secondary: colors.secondary,
    error: colors.error,
    warning: colors.warning,
    success: colors.success,
    info: colors.info,
    text: colors.text,
    background: colors.background,
    action: colors.action,
    divider: colors.divider,
    common: colors.common,
    grey: colors.grey,
  },
  typography: {
    htmlFontSize: 10,
    fontFamily:
      "'Roboto', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Oxygen', 'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue', sans-serif",
    h1: {
      fontSize: '3.2rem',
      fontWeight: 700,
      lineHeight: 1.2,
      color: colors.text.primary,
    },
    h2: {
      fontSize: '2.4rem',
      fontWeight: 600,
      lineHeight: 1.3,
      color: colors.text.primary,
    },
    h3: {
      fontSize: '2.0rem',
      fontWeight: 600,
      lineHeight: 1.3,
      color: colors.text.primary,
    },
    h4: {
      fontSize: '1.8rem',
      fontWeight: 600,
      lineHeight: 1.4,
      color: colors.text.primary,
    },
    h5: {
      fontSize: '1.6rem',
      fontWeight: 600,
      lineHeight: 1.4,
      color: colors.text.primary,
    },
    h6: {
      fontSize: '1.4rem',
      fontWeight: 600,
      lineHeight: 1.4,
      color: colors.text.primary,
    },
    subtitle1: {
      fontSize: '1.6rem',
      fontWeight: 400,
      lineHeight: 1.5,
      color: colors.text.primary,
    },
    subtitle2: {
      fontSize: '1.4rem',
      fontWeight: 500,
      lineHeight: 1.5,
      color: colors.text.secondary,
    },
    body1: {
      fontSize: '1.4rem',
      fontWeight: 400,
      lineHeight: 1.5,
      color: colors.text.primary,
    },
    body2: {
      fontSize: '1.3rem',
      fontWeight: 400,
      lineHeight: 1.5,
      color: colors.text.secondary,
    },
    caption: {
      fontSize: '1.2rem',
      fontWeight: 400,
      lineHeight: 1.4,
      color: colors.text.secondary,
    },
    overline: {
      fontSize: '1.1rem',
      fontWeight: 500,
      lineHeight: 1.5,
      color: colors.text.secondary,
    },
    button: {
      fontSize: '1.4rem',
      fontWeight: 500,
      lineHeight: 1.5,
      textTransform: 'none',
    },
  },
  components: {
    MuiTypography: {
      styleOverrides: {},
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          boxShadow: 'none',
        },
      },
    },
    MuiSwitch: {
      styleOverrides: {
        thumb: {
          boxShadow: colors.shadows.paper,
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          textTransform: 'none',
        },
      },
    },
    MuiSnackbarContent: {
      styleOverrides: {
        message: {
          fontSize: '1.3rem',
          fontWeight: 'bold',
          lineHeight: '1.5rem',
          color: colors.common.white,
        },
      },
    },
    // @ts-ignore
    MuiPickersDay: {
      styleOverrides: {
        root: {
          fontSize: '14px',
          color: colors.legacy.secondary[100],
          lineHeight: 'normal',
          letterSpacing: 'normal',
        },
        dayWithMargin: {
          fontSize: '14px',
          color: colors.legacy.secondary[100],
          lineHeight: 'normal',
          letterSpacing: 'normal',
        },
      },
    },
    MuiDayCalendar: {
      styleOverrides: {
        weekDayLabel: {
          fontSize: '14px',
          color: colors.legacy.secondary[100],
          lineHeight: 'normal',
          letterSpacing: 'normal',
        },
      },
    },
    MuiPickersCalendarHeader: {
      styleOverrides: {
        labelContainer: {
          fontSize: '14px',
          color: colors.legacy.secondary[100],
        },
      },
    },
    MuiYearCalendar: {
      styleOverrides: {
        root: {
          fontSize: '14px',
          color: colors.legacy.secondary[100],
        },
        button: {
          fontSize: '14px',
          color: colors.legacy.secondary[100],
        },
      },
    },
    MuiTimeClock: {
      styleOverrides: {
        root: {
          fontSize: '14px',
          color: colors.legacy.secondary[100],
        },
      },
    },
    MuiClock: {
      styleOverrides: {
        meridiemText: {
          fontSize: '12px',
        },
      },
    },
    MuiMultiSectionDigitalClock: {
      styleOverrides: {
        root: {
          fontSize: '14px',
          color: colors.legacy.secondary[100],
        },
      },
    },
    MuiAutocomplete: {
      styleOverrides: {
        noOptions: {
          fontSize: '14px',
        },
        option: {
          fontSize: '16px',
        },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          fontSize: '14px',
          color: colors.common.white,
          backgroundColor: colors.grey[700],
        },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          margin: 0,
          paddingTop: 11,
          paddingBottom: 11,
          fontSize: '16px',
        },
      },
    },
    MuiSvgIcon: {
      styleOverrides: {
        root: {
          fontSize: 24,
        },
        fontSizeLarge: {
          fontSize: 35,
        },
        fontSizeSmall: {
          fontSize: 20,
        },
      },
    },
    MuiTableRow: {
      styleOverrides: {
        head: {
          height: 56,
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          padding: '4px',
          borderBottom: 'none',
        },
      },
    },
    MuiDialogTitle: {
      styleOverrides: {
        root: {
          padding: '24px 20px',
        },
      },
    },
    MuiSelect: {
      styleOverrides: {
        select: {
          fontSize: 14,
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          boxShadow: colors.shadows.paper,
        },
      },
    },
    MuiMenu: {
      styleOverrides: {
        paper: {
          fontSize: '16px !important',
        },
      },
    },
    MuiBreadcrumbs: {
      styleOverrides: {
        root: {
          fontSize: '12px',
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        input: {
          fontSize: '16px',
          '&::placeholder': {
            fontSize: '16px',
            opacity: 1,
          },
        },
      },
    },
    // MUI X v8 Pickers TextField styling
    MuiPickersTextField: {
      styleOverrides: {
        root: {
          fontSize: '16px',
        },
      },
    },
    MuiPickersSectionList: {
      styleOverrides: {
        root: {
          fontSize: '16px',
        },
        section: {
          fontSize: '16px',
        },
        sectionContent: {
          fontSize: '16px',
        },
      },
    },
    MuiPickersInputBase: {
      styleOverrides: {
        root: {
          fontSize: '16px',
        },
        input: {
          fontSize: '16px',
        },
        sectionsContainer: {
          fontSize: '16px',
        },
      },
    },
    MuiDatePicker: openOnFieldClick,
    MuiDesktopDatePicker: openOnFieldClick,
    MuiDateTimePicker: openOnFieldClick,
    MuiDesktopDateTimePicker: openOnFieldClick,
    MuiTimePicker: openOnFieldClick,
    MuiDesktopTimePicker: openOnFieldClick,
  },
});
